<template>
  <div class="page" :style="themeStyle">
    <div v-if="!loaded" class="loading">Loading</div>
    <div v-if="loaded" class="root">

      <!-- Top bar -->
      <div class="topbar">
        <div class="topbar-inner">
          <div class="tb-left">
            <img v-if="loinfo && loinfo.main_logo" :src="loinfo.main_logo" class="tb-logo" />
            <div class="tb-names">
              <span class="tb-lo">{{ loName || "Loan Officer" }}</span>
              <span class="tb-x">×</span>
              <span class="tb-re">{{ realtorName || "Partner" }}</span>
            </div>
          </div>
          <a class="tb-cta" :href="applyRightLink">{{ heroPrimaryText }}</a>
        </div>
        <!-- Diagonal accent stripe -->
        <div class="diag-stripe"></div>
      </div>

      <!-- Main layout -->
      <div class="main-layout">

        <!-- Hero panel -->
        <section class="hero-panel">
          <div class="hero-inner">
            <div class="hero-kicker">
            
          
            </div>
            <h1 class="hero-h1" v-html="heroH1Html"></h1>
            <p class="hero-p">{{ heroBody }}</p>
            <div class="hero-btns">
              <a class="btn-primary" :href="applyRightLink">{{ heroPrimaryText }}</a>
              <a v-if="applyLeftLink" class="btn-outline" :href="applyLeftLink">{{ heroSecondaryText }}</a>
            </div>
            <!-- Quick links row -->
            <div class="quick-row">
              <a v-if="loinfo && loinfo.phone_number" class="quick-chip" :href="'tel:' + loinfo.phone_number">
                <span class="chip-icon">↗</span> Call
              </a>
              <a v-if="loinfo && loinfo.email" class="quick-chip" :href="'mailto:' + loinfo.email">
                <span class="chip-icon">↗</span> Email
              </a>
              <a v-if="realtor && realtor.website_url" class="quick-chip" :href="realtor.website_url" target="_blank" rel="noreferrer">
                <span class="chip-icon">↗</span> Partner Site
              </a>
            </div>
          </div>

          <!-- Diagonal right edge -->
          <div class="panel-cut"></div>
        </section>

        <!-- Sidebar -->
        <aside class="sidebar">

          <div class="scard lo-scard">
            <div class="scard-header">
              <span class="scard-label">Loan Officer</span>
              <div class="scard-accent-dot"></div>
            </div>
            <div class="scard-body">
              <img v-if="loinfo && loinfo.lo_image" :src="loinfo.lo_image" class="scard-img" />
              <div class="scard-info">
                <div class="scard-name">{{ loName }}</div>
                <div class="scard-role">{{ loTitle }}</div>
                <div v-if="loinfo && loinfo.nmls" class="scard-nmls">NMLS {{ (loinfo.nmls || "").trim() }}</div>
              </div>
            </div>
            <div class="scard-divider"></div>
            <div class="scard-contacts">
              <a v-if="loinfo && loinfo.phone_number" :href="'tel:' + loinfo.phone_number" class="scard-contact">{{ loinfo.phone_number }}</a>
              <a v-if="loinfo && loinfo.email" :href="'mailto:' + loinfo.email" class="scard-contact">{{ loinfo.email }}</a>
            </div>
            <a class="scard-btn" :href="applyRightLink">{{ heroPrimaryText }}</a>
          </div>

          <div class="scard re-scard">
            <div class="scard-header">
              <span class="scard-label re-label">Partner</span>
              <div class="scard-accent-dot re-dot"></div>
            </div>
            <div class="scard-body">
              <img v-if="realtor && realtor.headshot_url" :src="realtor.headshot_url" class="scard-img" />
              <div class="scard-info">
                <div class="scard-name">{{ realtorName }}</div>
                <div class="scard-role">{{ realtor && realtor.brokerage_name ? realtor.brokerage_name : "" }}</div>
              </div>
            </div>
            <div class="scard-divider re-divider"></div>
            <div class="scard-contacts">
              <a v-if="realtor && realtor.phone_number" :href="'tel:' + realtor.phone_number" class="scard-contact re-contact">{{ realtor.phone_number }}</a>
              <a v-if="realtor && realtor.email" :href="'mailto:' + realtor.email" class="scard-contact re-contact">{{ realtor.email }}</a>
              <a v-if="realtor && realtor.website_url" :href="realtor.website_url" class="scard-contact re-contact" target="_blank" rel="noreferrer">Website →</a>
            </div>
          </div>

        </aside>
      </div>

      <!-- Bottom rule with diagonal motif -->
      <div class="bottom-rule">
        <div class="br-diag"></div>
        <div class="br-text">{{ loName || "Your Loan Officer" }} · {{ loTitle || "NMLS Licensed" }}</div>
      </div>

    </div>
  </div>
</template>

<script>
import coBrandMixin from "../../mixins/coBrandMixin.js";
const BRAND_COLORS = {
  zonalending: { primary: "#eb610a", accent: "#646363" },
  radiantmortgage: { primary: "#1C1F39", accent: "#DB9457" },
  conradmortgage: { primary: "#4a4b4b", accent: "#d4af37" },
  theimgteam: { primary: "#ff6633", accent: "#4a4c54" },
  responsivemortgage: { primary: "#07212c", accent: "#FF7F47" },
  kpmortgage: { primary: "#0D2821", accent: "#34BFC6" },
  ignitemortgage: { primary: "#000000", accent: "#00b1eb" },
  goldstarfinancial: { primary: "#0b1726", accent: "#c69c39" },
};
function getBrandKey(email) {
  if (!email) return null;
  const d = email.slice(email.indexOf("@") + 1).toLowerCase();
  return d.split(".")[0] || null;
}
export default {
  name: "CoBrandBlueprint",
  mixins: [coBrandMixin],
  computed: {
    brandKey() { const k = getBrandKey(this.loinfo?.email); return (k && BRAND_COLORS[k]) ? k : "goldstarfinancial"; },
    palette() { return BRAND_COLORS[this.brandKey] || BRAND_COLORS.goldstarfinancial; },
    themeStyle() { return { "--p": this.palette.primary, "--a": this.palette.accent }; },
    
    heroH1Html() {
      const h = (this.realtor?.header1 || this.loinfo?.header1 || "").trim();
      if (!h) return "Ready to get<br/><em>started?</em>";
      const parts = h.split(",");
      if (parts.length > 1) {
        const a = parts[0].trim();
        const b = parts.slice(1).join(",").trim();
        return `${a}<br/><em>${b}</em>`;
      }
      return h;
    },
    
    heroHeading() { return this.loinfo?.text_section_heading || "About"; },
    heroPrimaryText() { return this.loinfo?.right_button_text || "Apply Now"; },
    heroSecondaryText() { return this.loinfo?.left_button_text || "Schedule Appointment"; },
    heroBody() {
      const m = (this.realtor?.main || this.loinfo?.main || "").trim();
      if (m) return m;
      const p = [this.loinfo?.text_section_body_p1, this.loinfo?.text_section_body_p2].filter(Boolean).join(" ").trim();
      return p || this.loinfo?.footer_slogan || "Start your application in minutes and we'll guide you from application through closing.";
    },
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&family=Bitter:ital,wght@0,400;0,700;1,400&display=swap');

.page {
  min-height: 100vh;
  background: #f4f6f9;
  color: #1a2332;
  font-family: 'Outfit', sans-serif;
  --p: #0b1726;
  --a: #c69c39;
  --bg: #f4f6f9;
  --card-bg: #ffffff;
  --border: rgba(26,35,50,0.1);
  --muted: #64748b;
  --a-soft: color-mix(in srgb, var(--a) 12%, #f4f6f9);
  --p-soft: color-mix(in srgb, var(--p) 6%, #f4f6f9);
}

.root { display: flex; flex-direction: column; min-height: 100vh; }

/* TOP BAR */
.topbar {
  background: var(--p);
  position: relative;
  overflow: hidden;
}
.topbar-inner {
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 40px; position: relative; z-index: 2;
}
.tb-left { display: flex; align-items: center; gap: 18px; }
.tb-logo { width: 160px; max-height:80px; object-fit: contain; filter: brightness(0) invert(1); opacity: 0.9; } 
.tb-names { font-size: 13px; display: flex; align-items: center; gap: 8px; }
.tb-lo { font-weight: 600; color: #fff; }
.tb-x { color: var(--a); }
.tb-re { color: rgba(255,255,255,0.55); }
.tb-cta {
  font-size: 13px; font-weight: 600; text-decoration: none;
  background: var(--a); color: var(--p);
  padding: 9px 20px; border-radius: 6px;
  transition: opacity 0.2s;
}
.tb-cta:hover { opacity: 0.88; }

/* DIAGONAL STRIPE — accent band cutting across the bottom of the topbar */
.diag-stripe {
  height: 5px;
  background: repeating-linear-gradient(
    -60deg,
    var(--a) 0px,
    var(--a) 12px,
    color-mix(in srgb, var(--a) 55%, var(--p)) 12px,
    color-mix(in srgb, var(--a) 55%, var(--p)) 24px
  );
}

/* MAIN LAYOUT */
.main-layout {
  display: grid;
  grid-template-columns: 1fr 380px;
  flex: 1;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  padding: 0;
  gap: 0;
  align-items: start;
}

/* HERO PANEL */
.hero-panel {
  padding: 60px 70px 60px 48px;
  position: relative;
  min-height: 540px;
  display: flex;
  align-items: center;
}
.hero-panel::after {
  content: "";
  position: absolute;
  top: 0; right: -1px; bottom: 0;
  width: 60px;
  background: #f4f6f9;
  clip-path: polygon(100% 0, 100% 100%, 0 100%);
  z-index: 2;
}
.hero-inner { position: relative; z-index: 1; max-width: 560px; }

.hero-kicker { display: flex; align-items: center; gap: 10px; margin-bottom: 16px; }
.kicker-line { width: 28px; height: 2px; background: var(--a); }
.hero-kicker span { font-size: 12px; font-weight: 600; letter-spacing: 0.2em; text-transform: uppercase; color: var(--a); }

.hero-h1 {
  font-family: 'Bitter', serif;
  font-size: clamp(36px, 4.8vw, 62px);
  font-weight: 700; line-height: 1.1;
  color: var(--p); margin: 0 0 18px;
}
.h1-accent { color: var(--a); font-style: italic; font-weight: 400; }
.hero-p { font-size: 15px; line-height: 1.72; color: var(--muted);  margin-bottom: 28px; }

.hero-btns { display: flex; gap: 10px; flex-wrap: wrap; margin-bottom: 24px; justify-content: center; }
.btn-primary {
  font-size: 14px; font-weight: 600; text-decoration: none;
  background: var(--p); color: #fff;
  padding: 13px 26px; border-radius: 8px;
  box-shadow: 0 4px 16px color-mix(in srgb, var(--p) 28%, transparent);
  transition: transform 0.18s, box-shadow 0.18s;
  width:55%
}
.btn-primary:hover { transform: translateY(-1px); box-shadow: 0 8px 22px color-mix(in srgb, var(--p) 32%, transparent); }
.btn-outline {
  font-size: 14px; font-weight: 500; text-decoration: none;
  background: transparent; color: var(--p);
  padding: 13px 26px; border-radius: 8px;
  border: 1.5px solid var(--border);
  transition: background 0.18s, border-color 0.18s;
}
.btn-outline:hover { background: var(--p-soft); border-color: color-mix(in srgb, var(--p) 20%, transparent); }

.quick-row { display: flex; gap: 8px; flex-wrap: wrap; }
.quick-chip {
  display: flex; align-items: center; gap: 5px;
  font-size: 12px; font-weight: 500; text-decoration: none;
  color: var(--muted); background: var(--card-bg);
  padding: 7px 14px; border-radius: 6px;
  border: 1px solid var(--border);
  transition: all 0.18s;
}
.quick-chip:hover { color: var(--p); border-color: color-mix(in srgb, var(--p) 20%, transparent); background: var(--p-soft); }
.chip-icon { color: var(--a); font-size: 11px; }

/* SIDEBAR */
.sidebar { padding: 40px 32px 40px 20px; display: flex; flex-direction: column; gap: 14px; }

.scard {
  background: var(--card-bg);
  border-radius: 14px;
  padding: 18px;
  border: 1px solid var(--border);
  box-shadow: 0 2px 14px rgba(26,35,50,0.06);
  position: relative;
  overflow: hidden;
  transition: box-shadow 0.2s;
}
.scard:hover { box-shadow: 0 6px 24px rgba(26,35,50,0.10); }

/* Diagonal corner accent */
.scard::after {
  content: "";
  position: absolute; top: 0; right: 0;
  width: 0; height: 0;
  border-style: solid;
  border-width: 0 32px 32px 0;
  border-color: transparent color-mix(in srgb, var(--p) 8%, transparent) transparent transparent;
}
.re-scard::after { border-color: transparent color-mix(in srgb, var(--a) 15%, transparent) transparent transparent; }

.scard-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; }
.scard-label { font-size: 10px; font-weight: 600; letter-spacing: 0.2em; text-transform: uppercase; color: var(--muted); }
.re-label { color: color-mix(in srgb, var(--a) 80%, var(--p)); }
.scard-accent-dot { width: 8px; height: 8px; border-radius: 999px; background: var(--p); }
.re-dot { background: var(--a); }

.scard-body { display: flex; gap: 12px; align-items: flex-start; }
.scard-img { width: 56px; height: 56px; border-radius: 10px; object-fit: cover; flex-shrink: 0; }
.scard-name { font-size: 17px; font-weight: 700; color: var(--p); }
.scard-role { font-size: 12px; color: var(--muted); margin-top: 2px; }
.scard-nmls { font-size: 11px; color: color-mix(in srgb, var(--muted) 70%, transparent); margin-top: 2px; }

.scard-divider { height: 1px; background: var(--border); margin: 14px 0; position: relative; }
.scard-divider::after {
  content: "";
  position: absolute; top: -1px; left: 0;
  width: 36px; height: 3px;
  background: var(--p);
  clip-path: polygon(0 0, 100% 0, 88% 100%, 0 100%);
}
.re-divider::after { background: var(--a); }

.scard-contacts { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 12px; }
.scard-contact {
  font-size: 11px; font-weight: 400; text-decoration: none;
  color: var(--p); background: var(--p-soft);
  padding: 5px 11px; border-radius: 6px;
  border: 1px solid color-mix(in srgb, var(--p) 10%, transparent);
  transition: background 0.18s;
}
.scard-contact:hover { background: color-mix(in srgb, var(--p) 12%, var(--bg)); }
.re-contact { color: color-mix(in srgb, var(--a) 85%, var(--p)); background: var(--a-soft); border-color: color-mix(in srgb, var(--a) 18%, transparent); }
.re-contact:hover { background: color-mix(in srgb, var(--a) 18%, var(--bg)); }

.scard-btn {
  display: block; text-align: center; text-decoration: none;
  background: var(--p); color: #fff;
  font-size: 13px; font-weight: 600; padding: 11px;
  border-radius: 8px; transition: opacity 0.2s;
}
.scard-btn:hover { opacity: 0.88; }

/* BOTTOM RULE */
.bottom-rule {
  border-top: 1px solid var(--border);
  display: flex; align-items: center; gap: 0;
  overflow: hidden;
}
.br-diag {
  width: 40px; flex-shrink: 0;
  height: 44px;
  background: var(--a);
  clip-path: polygon(0 0, 70% 0, 100% 100%, 0 100%);
}
.br-text { padding: 0 20px; font-size: 12px; color: var(--muted); letter-spacing: 0.05em; }

@media (max-width: 820px) {
  .main-layout { grid-template-columns: 1fr; }
  .hero-panel { padding: 40px 24px; min-height: auto; }
  .hero-panel::after { display: none; }
  .sidebar { padding: 24px; }
  .topbar-inner { padding: 14px 24px; }
}
</style>
